import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import PageHeader from "components/PageLayout";
import Skeleton from "components/Skeleton";
import ConfirmDialog from "components/ConfirmDialog";
import { useAuth, requireAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useToast } from "components/Toast";
import { useTicketTemplate, useTicketTemplates } from "hooks/useTicketTemplates";
import { useRequestTypesAdmin } from "hooks/useRequestTypesAdmin";
import { useFunctions } from "hooks/useFunctions";
import IntakeFieldRow from "components/Workflows/IntakeFieldRow";
import FlowStepRow from "components/Workflows/FlowStepRow";
import FlowPreview from "components/Workflows/FlowPreview";
import { uid, slugify } from "constants/ticketTemplates";
import { validateTemplateText, RESERVED_TEMPLATE_PLACEHOLDERS } from "utilities/ticketTemplateText";
import { requestTypeLabeler, requestTypeSelectOptions } from "utilities/requestTypes";
import {
  variantsForType,
  initialVariantMode,
  variantChoiceIssues,
  variantCreateFields,
  variantUpdateFields,
  suggestVariantKey,
  describeTemplateError,
} from "utilities/workflowVariants";
import TemplateErrorPanel from "components/Workflows/TemplateErrorPanel";
import {
  spawnWhenToEditor,
  spawnWhenToApi,
  visibleWhenToEditor,
  visibleWhenToApi,
  pruneSpawnWhen,
  conditionalDependencyWarnings,
  spawnWhenIssues,
  responseWarnings,
  describeSpawnWhen,
} from "utilities/conditionalSteps";
import {
  ArrowLeft,
  Plus,
  ListChecks,
  GitBranch,
  Eye,
  FileText,
  AlertTriangle,
  Type,
  Layers,
  Star,
  CopyPlus,
} from "lucide-react";

/* ── model helpers (UI identity ⇄ backend keys) ─────────────────────────── */

// Keys (step + intake-field) are internal plumbing, NOT author-facing. They are
// auto-derived from the title/label and deduped with a numeric suffix so they
// are ALWAYS unique + non-empty within the template. Deps/visibleWhen are
// tracked by `_id` (not key) and only converted to keys at serialize, so this
// on-the-fly derivation never disturbs the dependency graph. Run it after every
// mutation that touches titles/labels, add, reorder, or delete.
function deriveKeys(items, sourceField) {
  const fallback = sourceField === "title" ? "step" : "field";
  const seen = {};
  return items.map((it) => {
    const base = slugify(it[sourceField] || "") || fallback;
    let key = base;
    let n = 2;
    while (seen[key]) key = `${base}_${n++}`;
    seen[key] = true;
    return it.key === key ? it : { ...it, key };
  });
}

function blankDraft(requestTypeKey = "") {
  return {
    templateId: null,
    requestTypeKey,
    name: "",
    description: "",
    active: false,
    version: 1,
    audience: null,
    intakeFields: [],
    childSteps: [],
    titleTemplate: "",
    descriptionTemplate: "",
    // SOUND-785 — set from the API row on edit. A NEW draft's variant lives in
    // the editor's `variantChoice` instead (it is not a field of the draft
    // until the API assigns it).
    variantKey: null,
    variantLabel: "",
    isDefault: false,
  };
}

// Copy a template's CONTENT (intake, steps, name/description, prefill text)
// into a new draft — "start from an existing variant". Identity, version,
// state and variant are NOT copied: the new row is a draft of whichever
// variant the author picks. Fresh _ids keep the copy independent.
function copyContentInto(draft, source) {
  const h = hydrate(source);
  return {
    ...draft,
    name: h.name,
    description: h.description,
    intakeFields: h.intakeFields,
    childSteps: h.childSteps,
    titleTemplate: h.titleTemplate,
    descriptionTemplate: h.descriptionTemplate,
  };
}

function hydrate(t) {
  const fields = (t.intakeFields || []).map((f) => ({
    _id: uid(),
    key: f.key || "",
    label: f.label || "",
    type: f.type || "text",
    required: !!f.required,
    options: Array.isArray(f.options) ? f.options : [],
    otherKey: f.otherKey || "",
    gridCols: f.gridCols === 2 ? 2 : 1,
    placeholder: f.placeholder || "",
    helpText: f.helpText || "",
    visibleWhen: null,
    _raw: f.visibleWhen || null,
  }));
  const fieldIdByKey = {};
  fields.forEach((f) => f.key && (fieldIdByKey[f.key] = f._id));
  fields.forEach((f) => {
    f.visibleWhen = visibleWhenToEditor(f._raw, fieldIdByKey);
    delete f._raw;
  });

  const steps = (t.childSteps || []).map((s) => ({
    _id: uid(),
    key: s.key || "",
    title: s.title || "",
    description: s.description || "",
    kind: s.kind || "task",
    assigneeRole: s.assigneeRole || "",
    defaultPriority: s.defaultPriority || "normal",
    dependsOn: [],
    _raw: s.dependsOnStepKeys || [],
    // SOUND-546 — resolved to the intake field's _id (fields are hydrated
    // first), so a label edit that re-derives the key keeps the reference.
    spawnWhen: spawnWhenToEditor(s.spawnWhen, fieldIdByKey),
  }));
  const stepIdByKey = {};
  steps.forEach((s) => s.key && (stepIdByKey[s.key] = s._id));
  steps.forEach((s) => {
    s.dependsOn = (s._raw || []).map((k) => stepIdByKey[k]).filter(Boolean);
    delete s._raw;
  });

  return {
    templateId: t.templateId,
    requestTypeKey: t.requestTypeKey,
    name: t.name || "",
    description: t.description || "",
    active: !!t.active,
    version: t.version || 1,
    audience: t.audience || null,
    // Refs above are resolved by the stored keys FIRST (into _id), then keys are
    // re-derived — the _id-tracked deps/visibleWhen survive the re-key.
    intakeFields: deriveKeys(fields, "label"),
    childSteps: deriveKeys(steps, "title"),
    // SOUND-544 prefill templates. The API always returns both (null = unset).
    titleTemplate: t.titleTemplate || "",
    descriptionTemplate: t.descriptionTemplate || "",
    // SOUND-785. Reads always carry all three; a legacy row's label is null.
    variantKey: t.variantKey || "default",
    variantLabel: t.variantLabel || "",
    isDefault: t.isDefault === true,
  };
}

function serialize(draft) {
  const fieldKeyById = {};
  const fieldTypeById = {};
  draft.intakeFields.forEach((f) => {
    fieldKeyById[f._id] = f.key;
    fieldTypeById[f._id] = f.type;
  });
  const stepKeyById = {};
  draft.childSteps.forEach((s) => (stepKeyById[s._id] = s.key));

  return {
    requestTypeKey: draft.requestTypeKey,
    name: (draft.name || "").trim(),
    description: draft.description || null,
    // Blank → null (the API treats whitespace-only as unset too).
    titleTemplate: (draft.titleTemplate || "").trim() ? draft.titleTemplate : null,
    descriptionTemplate: (draft.descriptionTemplate || "").trim() ? draft.descriptionTemplate : null,
    // Edit only: the variant's NAME is editable on any row (the key is locked).
    // A new draft's blank label adds nothing; its variant fields come from
    // variantCreateFields at save.
    ...variantUpdateFields(draft.variantLabel),
    intakeFields: draft.intakeFields.map((f) => {
      const isOpt = f.type === "select" || f.type === "select-other";
      // SOUND-791: a number field's condition is saved as a number (the same
      // conversion spawnWhen uses), matching the number the modal sends.
      const visibleWhen = visibleWhenToApi(f.visibleWhen, fieldKeyById, fieldTypeById);
      return {
        key: (f.key || "").trim(),
        label: f.label,
        type: f.type,
        required: !!f.required,
        options: isOpt ? (f.options || []).filter((o) => (o || "").trim() !== "") : null,
        otherKey: f.type === "select-other" ? f.otherKey || null : null,
        visibleWhen,
        gridCols: f.gridCols === 2 ? 2 : 1,
        placeholder: f.placeholder || null,
        helpText: f.helpText || null,
      };
    }),
    childSteps: draft.childSteps.map((s) => ({
      key: (s.key || "").trim(),
      title: s.title,
      description: s.description || null,
      kind: s.kind || "task",
      assigneeRole: s.assigneeRole || null,
      defaultPriority: s.defaultPriority || "normal",
      dependsOnStepKeys: (s.dependsOn || []).map((id) => stepKeyById[id]).filter(Boolean),
      spawnWhen: spawnWhenToApi(s.spawnWhen, fieldKeyById, fieldTypeById),
    })),
  };
}

// After a reorder/delete, drop any step dep that no longer points at an EARLIER
// step (self-heals the earlier-only invariant). Returns { steps, prunedCount }.
function pruneSteps(steps) {
  const idxById = {};
  steps.forEach((s, i) => (idxById[s._id] = i));
  let prunedCount = 0;
  const next = steps.map((s, i) => {
    const kept = (s.dependsOn || []).filter((d) => (idxById[d] ?? Infinity) < i);
    if (kept.length !== (s.dependsOn || []).length) prunedCount += (s.dependsOn || []).length - kept.length;
    return kept.length === (s.dependsOn || []).length ? s : { ...s, dependsOn: kept };
  });
  return { steps: next, prunedCount };
}

// Same for intake visibleWhen (must reference an earlier field).
function pruneFields(fields) {
  const idxById = {};
  fields.forEach((f, i) => (idxById[f._id] = i));
  let prunedCount = 0;
  const next = fields.map((f, i) => {
    if (f.visibleWhen && f.visibleWhen.fieldId) {
      const vi = idxById[f.visibleWhen.fieldId];
      if (vi === undefined || vi >= i) {
        prunedCount += 1;
        return { ...f, visibleWhen: null };
      }
    }
    return f;
  });
  return { fields: next, prunedCount };
}

function keyErrorsOf(items) {
  const seen = {};
  const errs = {};
  items.forEach((it) => {
    const k = (it.key || "").trim();
    if (!k) errs[it._id] = "Key is required";
    else if (seen[k]) errs[it._id] = `Duplicate key "${k}"`;
    else seen[k] = true;
  });
  return errs;
}

/* ── component ──────────────────────────────────────────────────────────── */

// Keyed by route so moving between templates, or from a template to a new
// draft ("New version", "Add variant"), mounts a fresh editor instead of
// carrying one draft's state into another (both routes render this component,
// so React would otherwise reuse the instance).
function WorkflowEditorRoute() {
  const { templateId } = useParams();
  const location = useLocation();
  const routeKey = !templateId || templateId === "new" ? `new${location.search}` : templateId;
  return <WorkflowEditor key={routeKey} />;
}

function WorkflowEditor() {
  const { templateId } = useParams();
  const isNew = !templateId || templateId === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();
  const authUser = { id: user.id, accessToken: user.accessToken };
  const { toast } = useToast();

  const { template, loading } = useTicketTemplate(templateId);
  const { templates, loading: templatesLoading, refetch: refetchTemplates } = useTicketTemplates();
  // Admin vocabulary (includeInactive) so a workflow on a since-deactivated
  // type still shows its label; only ACTIVE types are offered for a new draft
  // (the API refuses to create on a deactivated type).
  const { requestTypes: adminRequestTypes } = useRequestTypesAdmin({ includeInactive: true });
  const { functions } = useFunctions({ include: undefined });

  const [draft, setDraft] = useState(() => blankDraft(searchParams.get("requestType") || ""));
  const [savedSnapshot, setSavedSnapshot] = useState(null); // JSON string of last-saved serialize()
  const [saving, setSaving] = useState(false);
  const [activateConfirm, setActivateConfirm] = useState(false);
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  // The last refused save/activate/deactivate/make-default, shown in full until
  // dismissed (SOUND-785: every variant code reaches the screen, never only a
  // toast). Seeded from navigation state when a create succeeded but its
  // activation did not, so the refusal survives the move to the new URL.
  const [serverError, setServerError] = useState(() => location.state?.serverError || null);
  // SOUND-546 — the API's NON-blocking `warnings` from the last save/activate
  // (e.g. a step that always runs depending on a conditional one). Carried
  // across the create→edit remount like serverError.
  const [serverWarnings, setServerWarnings] = useState(() => location.state?.serverWarnings || []);
  const [busyTemplateId, setBusyTemplateId] = useState(null);
  const hydratedRef = useRef(false);

  // SOUND-785 — which variant a NEW draft belongs to. Initialised once the
  // type's templates are known, and again whenever the request type changes.
  const [variantChoice, setVariantChoice] = useState({ mode: "first", variantKey: "", variantLabel: "" });
  const [variantKeyTouched, setVariantKeyTouched] = useState(false);
  const [copySourceId, setCopySourceId] = useState("");
  const variantInitRef = useRef(null);

  // Hydrate once the template loads (edit).
  useEffect(() => {
    if (!isNew && template && !hydratedRef.current) {
      const d = hydrate(template);
      setDraft(d);
      setSavedSnapshot(JSON.stringify(serialize(d)));
      hydratedRef.current = true;
    }
  }, [isNew, template]);

  const rtLabelOf = useMemo(() => requestTypeLabeler(adminRequestTypes), [adminRequestTypes]);
  const requestTypeLabel = draft.requestTypeKey ? rtLabelOf(draft.requestTypeKey) : "—";
  // Every ACTIVE request type is selectable (SOUND-785 removed the SOUND-506
  // `claimedKeys` filter): picking a type that already has workflows asks
  // whether this is a new version of a variant or a new variant.
  const selectableTypes = useMemo(
    () => requestTypeSelectOptions(adminRequestTypes, draft.requestTypeKey),
    [adminRequestTypes, draft.requestTypeKey]
  );

  const typeTemplates = useMemo(
    () => templates.filter((t) => t.requestTypeKey === draft.requestTypeKey),
    [templates, draft.requestTypeKey]
  );
  const typeVariants = useMemo(
    () => variantsForType(templates, draft.requestTypeKey, requestTypeLabel),
    [templates, draft.requestTypeKey, requestTypeLabel]
  );

  // New draft: pick the variant mode for the chosen type (URL hints first —
  // `variantKey` = new version of it, `newVariant=1` = a new variant), and
  // apply `copyFrom` once. Re-runs only when the request type changes.
  useEffect(() => {
    if (!isNew || templatesLoading) return;
    const key = draft.requestTypeKey || "";
    if (variantInitRef.current === key) return;
    const firstInit = variantInitRef.current === null;
    variantInitRef.current = key;
    const init = initialVariantMode(typeVariants, {
      variantKey: firstInit ? searchParams.get("variantKey") || "" : "",
      newVariant: firstInit && searchParams.get("newVariant") === "1",
    });
    setVariantChoice({ mode: init.mode, variantKey: init.variantKey, variantLabel: "" });
    setVariantKeyTouched(init.mode === "new" && !!init.variantKey);
    const copyFrom = firstInit ? searchParams.get("copyFrom") : null;
    const source = copyFrom ? typeTemplates.find((t) => t.templateId === copyFrom) : null;
    if (source) {
      setDraft((d) => copyContentInto(d, source));
      setCopySourceId(source.templateId);
    } else {
      setCopySourceId("");
    }
    // searchParams is read only on the first init (the URL's intent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, templatesLoading, draft.requestTypeKey, typeVariants, typeTemplates]);

  const variantIssues = useMemo(
    () => (isNew ? variantChoiceIssues(variantChoice, typeVariants) : []),
    [isNew, variantChoice, typeVariants]
  );

  const fieldKeyErrors = useMemo(() => keyErrorsOf(draft.intakeFields), [draft.intakeFields]);
  const stepKeyErrors = useMemo(() => keyErrorsOf(draft.childSteps), [draft.childSteps]);

  const structuralOk =
    !!draft.name.trim() &&
    !!draft.requestTypeKey &&
    variantIssues.length === 0 &&
    Object.keys(fieldKeyErrors).length === 0 &&
    Object.keys(stepKeyErrors).length === 0;

  // Intake keys are derived from labels, so the placeholders an author may use
  // move as labels change — warn live, mirroring the API's save-time check.
  const intakeKeys = useMemo(() => draft.intakeFields.map((f) => f.key).filter(Boolean), [draft.intakeFields]);
  const templateTextIssues = useMemo(
    () => [
      ...validateTemplateText(draft.titleTemplate || null, { intakeFieldKeys: intakeKeys, field: "titleTemplate" }),
      ...validateTemplateText(draft.descriptionTemplate || null, {
        intakeFieldKeys: intakeKeys,
        field: "descriptionTemplate",
      }),
    ],
    [draft.titleTemplate, draft.descriptionTemplate, intakeKeys]
  );

  // SOUND-546 — conditional steps, checked on the API shape the server sees.
  const serializedSteps = useMemo(() => serialize(draft).childSteps, [draft]);
  const conditionWarnings = useMemo(() => conditionalDependencyWarnings(serializedSteps), [serializedSteps]);
  const conditionTextById = useMemo(() => {
    const map = {};
    draft.childSteps.forEach((st, i) => {
      const text = describeSpawnWhen(serializedSteps[i]?.spawnWhen, draft.intakeFields);
      if (text) map[st._id] = text;
    });
    return map;
  }, [draft.childSteps, draft.intakeFields, serializedSteps]);

  const activationIssues = useMemo(() => {
    const issues = [];
    if (!draft.name.trim()) issues.push("Template needs a name.");
    if (!draft.requestTypeKey) issues.push("Pick a request type.");
    variantIssues.forEach((e) => issues.push(`Variant: ${e}`));
    Object.values(fieldKeyErrors).forEach((e) => issues.push(`Intake field: ${e}`));
    Object.values(stepKeyErrors).forEach((e) => issues.push(`Flow step: ${e}`));
    draft.intakeFields.forEach((f) => {
      if (f.type === "select" || f.type === "select-other") {
        if ((f.options || []).filter((o) => (o || "").trim()).length === 0)
          issues.push(`Field "${f.label || f.key}" needs at least one option.`);
      }
      if (f.type === "select-other" && !(f.otherKey || "").trim())
        issues.push(`Field "${f.label || f.key}" needs a free-text key.`);
    });
    templateTextIssues.forEach((e) => issues.push(e));
    spawnWhenIssues(serializedSteps).forEach((e) => issues.push(e));
    return issues;
  }, [draft, variantIssues, fieldKeyErrors, stepKeyErrors, templateTextIssues, serializedSteps]);

  const currentSnapshot = JSON.stringify(serialize(draft));
  const dirty =
    savedSnapshot === null
      ? !!draft.name ||
        draft.intakeFields.length > 0 ||
        draft.childSteps.length > 0 ||
        !!draft.titleTemplate ||
        !!draft.descriptionTemplate ||
        !!variantChoice.variantLabel
      : currentSnapshot !== savedSnapshot;

  // Warn on browser close/refresh with unsaved changes.
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  /* ── mutators ── (every intake/step array update re-derives keys, so keys
     stay unique + non-empty in state without an author-facing input) */
  const patchField = (id, patch) =>
    setDraft((d) => ({
      ...d,
      intakeFields: deriveKeys(
        d.intakeFields.map((f) => (f._id === id ? { ...f, ...patch } : f)),
        "label"
      ),
    }));
  const addField = () =>
    setDraft((d) => ({
      ...d,
      intakeFields: deriveKeys(
        [
          ...d.intakeFields,
          { _id: uid(), key: "", label: "", type: "text", required: false, options: [], otherKey: "", gridCols: 1, placeholder: "", helpText: "", visibleWhen: null },
        ],
        "label"
      ),
    }));
  const moveField = (index, dir) =>
    setDraft((d) => {
      const arr = [...d.intakeFields];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      const { fields, prunedCount } = pruneFields(arr);
      if (prunedCount) toast({ status: "info", title: `Removed ${prunedCount} visibility rule(s)`, description: "A condition no longer referenced an earlier field." });
      return { ...d, intakeFields: deriveKeys(fields, "label") };
    });
  const deleteField = (index) =>
    setDraft((d) => {
      const { fields, prunedCount } = pruneFields(d.intakeFields.filter((_, i) => i !== index));
      if (prunedCount) toast({ status: "info", title: `Removed ${prunedCount} visibility rule(s)`, description: "They referenced the deleted field." });
      // SOUND-546 — a step condition on the deleted field goes too.
      const { steps, prunedCount: stepPruned } = pruneSpawnWhen(d.childSteps, fields.map((f) => f._id));
      if (stepPruned) toast({ status: "info", title: `Removed ${stepPruned} step condition(s)`, description: "They referenced the deleted field." });
      return { ...d, intakeFields: deriveKeys(fields, "label"), childSteps: steps };
    });

  const patchStep = (id, patch) =>
    setDraft((d) => ({
      ...d,
      childSteps: deriveKeys(
        d.childSteps.map((s) => (s._id === id ? { ...s, ...patch } : s)),
        "title"
      ),
    }));
  const addStep = () =>
    setDraft((d) => ({
      ...d,
      childSteps: deriveKeys(
        [
          ...d.childSteps,
          { _id: uid(), key: "", title: "", description: "", kind: "task", assigneeRole: "", defaultPriority: "normal", dependsOn: [], spawnWhen: null },
        ],
        "title"
      ),
    }));
  const moveStep = (index, dir) =>
    setDraft((d) => {
      const arr = [...d.childSteps];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      const { steps, prunedCount } = pruneSteps(arr);
      if (prunedCount) toast({ status: "info", title: `Removed ${prunedCount} dependency(ies)`, description: "A step no longer runs after an earlier step." });
      return { ...d, childSteps: deriveKeys(steps, "title") };
    });
  const deleteStep = (index) =>
    setDraft((d) => {
      const { steps, prunedCount } = pruneSteps(d.childSteps.filter((_, i) => i !== index));
      if (prunedCount) toast({ status: "info", title: `Removed ${prunedCount} dependency(ies)`, description: "They referenced the deleted step." });
      return { ...d, childSteps: deriveKeys(steps, "title") };
    });

  /* ── variant choice (new drafts) ── */
  const setVariantMode = (mode) => {
    setVariantKeyTouched(false);
    if (mode === "existing") {
      const def = typeVariants.find((v) => v.isDefault) || typeVariants[0];
      setVariantChoice({ mode, variantKey: def ? def.variantKey : "", variantLabel: "" });
    } else {
      setVariantChoice({ mode, variantKey: "", variantLabel: "" });
    }
  };
  const setNewVariantLabel = (label) =>
    setVariantChoice((c) => ({
      ...c,
      variantLabel: label,
      // The key follows the name until the author edits it.
      variantKey: variantKeyTouched ? c.variantKey : suggestVariantKey(label),
    }));

  const applyCopySource = (id) => {
    setCopySourceId(id);
    if (!id) return; // "Blank" leaves the current content alone
    const source = templates.find((t) => t.templateId === id);
    if (source) {
      setDraft((d) => copyContentInto(d, source));
      toast({ status: "info", title: "Copied intake, flow and text", description: "Nothing is saved until you save the draft." });
    }
  };

  /* ── save / activate / deactivate / make default ── */
  const save = async ({ activate }) => {
    if (!structuralOk) {
      setShowIssues(true);
      toast({ status: "warning", title: "Fix the highlighted fields first" });
      return;
    }
    setSaving(true);
    setServerError(null);
    setServerWarnings([]);
    let createdId = null;
    let warnings = [];
    try {
      let tid = draft.templateId;
      let saved;
      if (tid) {
        const res = await makeApiRequest("put", apiRoutes.updateTicketTemplate(tid), authUser, companyId, serialize(draft));
        saved = res.data?.data;
        warnings = responseWarnings(res.data);
      } else {
        const body = { ...serialize(draft), ...variantCreateFields(variantChoice) };
        const res = await makeApiRequest("post", apiRoutes.createTicketTemplate, authUser, companyId, body);
        saved = res.data?.data;
        warnings = responseWarnings(res.data);
        tid = saved?.templateId;
        createdId = tid;
      }
      if (activate && tid) {
        const res = await makeApiRequest("post", apiRoutes.activateTicketTemplate(tid), authUser, companyId);
        saved = res.data?.data;
        // Activation re-validates; its warnings supersede the save's.
        const activateWarnings = responseWarnings(res.data);
        if (activateWarnings.length) warnings = activateWarnings;
      }
      toast({ status: "success", title: activate ? "Template activated" : "Draft saved" });
      if (isNew && tid) {
        // The route change remounts the editor, which loads the saved row.
        navigate(`/settings/workflows/${tid}`, { replace: true, state: warnings.length ? { serverWarnings: warnings } : undefined });
        return;
      }
      setServerWarnings(warnings);
      const d = hydrate(saved);
      setDraft(d);
      setSavedSnapshot(JSON.stringify(serialize(d)));
      hydratedRef.current = true;
      setShowIssues(false);
      refetchTemplates();
    } catch (err) {
      const model = describeTemplateError(err, activate ? "Activation failed" : "Save failed");
      toast({ status: "destructive", title: model.title, description: model.message });
      if (model.code === "VARIANT_KEY_REQUIRED" || model.code === "VARIANT_ACTIVATION_CONFLICT") refetchTemplates();
      if (isNew && createdId) {
        // The draft WAS created; only activation failed. Move to it (so a retry
        // activates this row instead of creating a duplicate) and carry the
        // refusal across the remount.
        navigate(`/settings/workflows/${createdId}`, { replace: true, state: { serverError: model, serverWarnings: warnings } });
        return;
      }
      setServerError(model);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setSaving(true);
    setServerError(null);
    try {
      const res = await makeApiRequest("post", apiRoutes.deactivateTicketTemplate(draft.templateId), authUser, companyId);
      const d = hydrate(res.data?.data);
      setDraft(d);
      setSavedSnapshot(JSON.stringify(serialize(d)));
      toast({ status: "success", title: "Template deactivated" });
      refetchTemplates();
    } catch (err) {
      const model = describeTemplateError(err, "Failed to deactivate");
      toast({ status: "destructive", title: model.title, description: model.message });
      setServerError(model);
    } finally {
      setSaving(false);
    }
  };

  // Make `target` ({templateId, variantLabel}) the type's default — this row,
  // or (from the DEFAULT_VARIANT_REQUIRED panel) another live variant.
  const makeDefault = async (target) => {
    if (!target?.templateId) return;
    setBusyTemplateId(target.templateId);
    try {
      await makeApiRequest("post", apiRoutes.makeDefaultTicketTemplate(target.templateId), authUser, companyId);
      const isThis = target.templateId === draft.templateId;
      // isDefault is not part of the saved snapshot, so this does not dirty the form.
      setDraft((d) => ({ ...d, isDefault: isThis }));
      toast({
        status: "success",
        title: `"${target.variantLabel || target.variantKey || "Variant"}" is now the default`,
        description: isThis ? undefined : "This variant can now be deactivated.",
      });
      setServerError(null);
      refetchTemplates();
    } catch (err) {
      const model = describeTemplateError(err, "Could not change the default");
      toast({ status: "destructive", title: model.title, description: model.message });
      setServerError(model);
    } finally {
      setBusyTemplateId(null);
    }
  };

  const leave = () => navigate("/settings/workflows");
  const goTo = (url) => (dirty ? setDiscardConfirm(url) : navigate(url));
  const typeQs = `requestType=${encodeURIComponent(draft.requestTypeKey)}`;

  if (!isNew && loading) {
    return (
      <div>
        <PageHeader title={<Skeleton.Rect className="h-8 w-64" />} />
        <div className="container space-y-6 py-6">
          <Skeleton.Rect className="h-40 w-full" />
          <Skeleton.Rect className="h-40 w-full" />
          <Skeleton.Rect className="h-40 w-full" />
        </div>
      </div>
    );
  }

  const audienceLabel = draft.audience
    ? draft.audience.includes("client")
      ? "Internal + Client"
      : "Internal"
    : isNew
    ? "Derived from request type on save"
    : "—";

  const variantName = isNew
    ? variantChoice.mode === "existing"
      ? typeVariants.find((v) => v.variantKey === variantChoice.variantKey)?.variantLabel || ""
      : variantChoice.variantLabel.trim()
    : draft.variantLabel.trim() || requestTypeLabel;
  const liveSiblingCount = typeVariants.filter((v) => v.live && v.variantKey !== (isNew ? variantChoice.variantKey : draft.variantKey)).length;

  return (
    <div>
      <PageHeader
        sticky
        title={
          <span className="inline-flex items-center gap-2">
            {requestTypeLabel}
            {variantName && variantName !== requestTypeLabel && (
              <span className="font-normal text-muted-foreground">· {variantName}</span>
            )}
            {dirty && <span className="h-2 w-2 rounded-full bg-warning" title="Unsaved changes" />}
          </span>
        }
        badge={
          <span className="inline-flex items-center gap-1.5">
            <span className={`badge ${draft.active ? "badge-success" : "badge-warning"}`}>
              {draft.active ? "Active" : "Draft"}
              {draft.templateId ? ` · v${draft.version}` : ""}
            </span>
            {draft.isDefault && (
              <span className="badge badge-brand inline-flex items-center gap-1">
                <Star className="h-3 w-3" />
                Default
              </span>
            )}
          </span>
        }
        subtitle={
          <button
            onClick={() => (dirty ? setDiscardConfirm(true) : leave())}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Ticket Workflows
          </button>
        }
        actions={
          <div className="flex items-center gap-2">
            <button className="btn btn-ghost" disabled={saving} onClick={() => (dirty ? setDiscardConfirm(true) : leave())}>
              Discard
            </button>
            <button className="btn btn-secondary" disabled={saving || !dirty} onClick={() => save({ activate: false })}>
              Save draft
            </button>
            {draft.active ? (
              <button className="btn btn-outline" disabled={saving} onClick={deactivate}>
                Deactivate
              </button>
            ) : (
              <button
                className="btn btn-primary"
                disabled={saving}
                onClick={() => {
                  if (activationIssues.length > 0) {
                    setShowIssues(true);
                    toast({ status: "warning", title: "Resolve the listed issues before activating" });
                  } else {
                    setActivateConfirm(true);
                  }
                }}
              >
                Save &amp; activate
              </button>
            )}
          </div>
        }
      />

      <div className="container space-y-6 py-6">
        {/* Every refusal from the API, in full, until dismissed. The
            DEFAULT_VARIANT_REQUIRED panel lists the other live variants with a
            "Make this the default" action, which is the way out of it. */}
        <TemplateErrorPanel
          error={serverError}
          onDismiss={() => setServerError(null)}
          onMakeDefault={makeDefault}
          busyTemplateId={busyTemplateId}
        />

        {/* Validation summary (on blocked activate / structural issues) */}
        {showIssues && activationIssues.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.06] p-4">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Resolve before activating
            </div>
            <ul className="list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
              {activationIssues.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        {/* (a) Metadata */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <FileText className="h-5 w-5 text-brand" />
            Request type &amp; metadata
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Request type</span>
              {isNew ? (
                <select
                  className="select"
                  value={draft.requestTypeKey}
                  onChange={(e) => setDraft((d) => ({ ...d, requestTypeKey: e.target.value }))}
                >
                  <option value="">Select a request type…</option>
                  {selectableTypes.map((rt) => (
                    <option key={rt.key} value={rt.key} disabled={!rt.active}>
                      {rt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
                  <span className="text-sm text-foreground">{requestTypeLabel}</span>
                  <span className="font-mono text-xs text-muted-foreground">{draft.requestTypeKey}</span>
                </div>
              )}
            </label>
            <div className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Audience</span>
              <div className="flex items-center rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                {audienceLabel}
                <span className="ml-2 text-xs">(from the request-type vocabulary)</span>
              </div>
            </div>
            <label className="flex flex-col text-sm md:col-span-2">
              <span className="mb-1 font-medium text-foreground">Name</span>
              <input
                className="input"
                placeholder="e.g. Descriptor Change — standard flow"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              />
            </label>
            <label className="flex flex-col text-sm md:col-span-2">
              <span className="mb-1 font-medium text-foreground">Description</span>
              <textarea
                className="textarea min-h-[72px]"
                placeholder="What this template's intake + flow accomplishes."
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </label>
          </div>
        </section>

        {/* (a1) Variant (SOUND-785) */}
        {draft.requestTypeKey && (
          <section className="surface rounded-xl ring-1 ring-border p-6">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <Layers className="h-5 w-5 text-brand" />
                Variant
              </h2>
              {!isNew && draft.templateId && (
                <div className="flex flex-wrap items-center gap-2">
                  {draft.active && !draft.isDefault && (
                    <button
                      className="btn btn-outline h-8 px-3 text-xs"
                      disabled={busyTemplateId === draft.templateId}
                      onClick={() => makeDefault({ templateId: draft.templateId, variantLabel: variantName })}
                    >
                      <Star className="h-3.5 w-3.5" />
                      Make default
                    </button>
                  )}
                  <button
                    className="btn btn-outline h-8 px-3 text-xs"
                    onClick={() =>
                      goTo(
                        `/settings/workflows/new?${typeQs}&variantKey=${encodeURIComponent(draft.variantKey)}&copyFrom=${draft.templateId}`
                      )
                    }
                  >
                    <CopyPlus className="h-3.5 w-3.5" />
                    New version
                  </button>
                  <button
                    className="btn btn-outline h-8 px-3 text-xs"
                    onClick={() => goTo(`/settings/workflows/new?${typeQs}&newVariant=1&copyFrom=${draft.templateId}`)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add variant
                  </button>
                </div>
              )}
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              A request type can run several named workflows. Its <strong>default</strong> runs whenever nobody
              picks — clients, Telegram, and any internal ticket where the picker is left alone. Activating a
              version replaces only the earlier version of the same variant.
            </p>

            {isNew ? (
              typeVariants.length === 0 ? (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="text-sm text-muted-foreground md:col-span-2">
                    This is the first workflow for {requestTypeLabel}. It becomes the type's default variant when
                    activated.
                  </div>
                  <label className="flex flex-col text-sm">
                    <span className="mb-1 font-medium text-foreground">
                      Variant name <span className="text-muted-foreground">(optional)</span>
                    </span>
                    <input
                      className="input"
                      placeholder={requestTypeLabel}
                      value={variantChoice.variantLabel}
                      onChange={(e) => setVariantChoice((c) => ({ ...c, variantLabel: e.target.value }))}
                    />
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col gap-2 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="variantMode"
                        checked={variantChoice.mode === "existing"}
                        onChange={() => setVariantMode("existing")}
                      />
                      <span className="text-foreground">New version of an existing variant</span>
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="variantMode"
                        checked={variantChoice.mode === "new"}
                        onChange={() => setVariantMode("new")}
                      />
                      <span className="text-foreground">New variant</span>
                    </label>
                  </div>

                  {variantChoice.mode === "existing" ? (
                    <label className="flex flex-col text-sm md:w-1/2">
                      <span className="mb-1 font-medium text-foreground">Variant</span>
                      <select
                        className="select"
                        value={variantChoice.variantKey}
                        onChange={(e) => setVariantChoice((c) => ({ ...c, variantKey: e.target.value }))}
                      >
                        {typeVariants.map((v) => (
                          <option key={v.variantKey} value={v.variantKey}>
                            {v.variantLabel}
                            {v.isDefault ? " (default)" : ""}
                            {v.live ? ` — live v${v.live.version}` : " — not live"}
                          </option>
                        ))}
                      </select>
                      <span className="mt-1 text-xs text-muted-foreground">
                        Activating this draft replaces that variant's live version. Other variants stay live.
                      </span>
                    </label>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <label className="flex flex-col text-sm">
                        <span className="mb-1 font-medium text-foreground">Variant name</span>
                        <input
                          className="input"
                          placeholder="e.g. Net new (no Disputifier instance)"
                          value={variantChoice.variantLabel}
                          onChange={(e) => setNewVariantLabel(e.target.value)}
                        />
                      </label>
                      <label className="flex flex-col text-sm">
                        <span className="mb-1 font-medium text-foreground">Variant key</span>
                        <input
                          className="input font-mono text-sm"
                          placeholder="net_new"
                          value={variantChoice.variantKey}
                          onChange={(e) => {
                            setVariantKeyTouched(true);
                            setVariantChoice((c) => ({ ...c, variantKey: e.target.value }));
                          }}
                        />
                        <span className="mt-1 text-xs text-muted-foreground">
                          Permanent — tickets record it for filtering and reporting.
                        </span>
                      </label>
                    </div>
                  )}

                  <label className="flex flex-col text-sm md:w-1/2">
                    <span className="mb-1 font-medium text-foreground">Start from</span>
                    <select className="select" value={copySourceId} onChange={(e) => applyCopySource(e.target.value)}>
                      <option value="">Blank (or what is already in the form)</option>
                      {typeVariants.map((v) => (
                        <option key={v.variantKey} value={v.primary.templateId}>
                          Copy “{v.variantLabel}” {v.live ? `(live v${v.live.version})` : "(latest draft)"}
                        </option>
                      ))}
                    </select>
                  </label>

                  {variantIssues.length > 0 && (
                    <ul className="space-y-0.5 text-xs text-destructive">
                      {variantIssues.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="flex flex-col text-sm">
                  <span className="mb-1 font-medium text-foreground">Variant name</span>
                  <input
                    className="input"
                    placeholder={requestTypeLabel}
                    value={draft.variantLabel}
                    onChange={(e) => setDraft((d) => ({ ...d, variantLabel: e.target.value }))}
                  />
                  <span className="mt-1 text-xs text-muted-foreground">
                    Saved with the draft. Shown in the create-ticket picker and on tickets.
                  </span>
                </label>
                <div className="flex flex-col text-sm">
                  <span className="mb-1 font-medium text-foreground">Variant key</span>
                  <div className="flex items-center rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-xs text-muted-foreground">
                    {draft.variantKey}
                    <span className="ml-2 font-sans">(locked)</span>
                  </div>
                  {liveSiblingCount > 0 && (
                    <span className="mt-1 text-xs text-muted-foreground">
                      {liveSiblingCount} other live {liveSiblingCount === 1 ? "variant" : "variants"} of {requestTypeLabel}.
                    </span>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* (a2) Title / description prefill (SOUND-544) */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold">
            <Type className="h-5 w-5 text-brand" />
            Ticket title &amp; description
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Optional. Prefills the create form; the agent can still edit both. Placeholders:{" "}
            {[...RESERVED_TEMPLATE_PLACEHOLDERS, ...intakeKeys].map((k, i) => (
              <React.Fragment key={k}>
                {i > 0 && " "}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">{`{{${k}}}`}</code>
              </React.Fragment>
            ))}
            . <code className="font-mono text-xs">{"{{variant}}"}</code> reads as this variant's name (the
            request-type label when it has none); an unanswered field renders blank.
          </p>
          <div className="grid grid-cols-1 gap-4">
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Title template</span>
              <input
                className="input font-mono text-sm"
                placeholder="e.g. {{requestType}} — {{client}}"
                value={draft.titleTemplate}
                onChange={(e) => setDraft((d) => ({ ...d, titleTemplate: e.target.value }))}
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 font-medium text-foreground">Description template</span>
              <textarea
                className="textarea min-h-[96px] font-mono text-sm"
                placeholder="Context the agent should start from."
                value={draft.descriptionTemplate}
                onChange={(e) => setDraft((d) => ({ ...d, descriptionTemplate: e.target.value }))}
              />
            </label>
            {templateTextIssues.length > 0 && (
              <ul className="space-y-0.5 text-xs text-destructive">
                {templateTextIssues.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* (b) Intake fields */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <ListChecks className="h-5 w-5 text-brand" />
              Intake fields
              <span className="text-sm font-normal text-muted-foreground">
                · {draft.intakeFields.length}
              </span>
            </h2>
            <button className="btn btn-outline" onClick={addField}>
              <Plus className="h-4 w-4" />
              Add field
            </button>
          </div>
          {draft.intakeFields.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border py-8 text-center">
              <p className="text-sm font-medium text-foreground">No intake fields yet</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Add the questions asked when this ticket is created (optional — a template can be pure flow).
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {draft.intakeFields.map((f, i) => (
                <IntakeFieldRow
                  key={f._id}
                  field={f}
                  index={i}
                  total={draft.intakeFields.length}
                  earlierFields={draft.intakeFields.slice(0, i)}
                  keyError={fieldKeyErrors[f._id]}
                  onChange={(patch) => patchField(f._id, patch)}
                  onMove={(dir) => moveField(i, dir)}
                  onDelete={() => deleteField(i)}
                />
              ))}
            </div>
          )}
        </section>

        {/* (c) Flow steps */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <GitBranch className="h-5 w-5 text-brand" />
              Flow
              <span className="text-sm font-normal text-muted-foreground">
                · {draft.childSteps.length} {draft.childSteps.length === 1 ? "step" : "steps"}
              </span>
            </h2>
            <button className="btn btn-outline" onClick={addStep}>
              <Plus className="h-4 w-4" />
              Add step
            </button>
          </div>
          {/* SOUND-546 — non-blocking: saving and activating still work. Live
              client-side check first, then whatever the last save returned. */}
          {(conditionWarnings.length > 0 || serverWarnings.length > 0) && (
            <div role="status" className="mb-4 rounded-lg border border-warning/40 bg-warning/10 p-3">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground">
                <AlertTriangle className="h-4 w-4 text-warning" />
                Check the conditional steps
                <span className="text-xs font-normal text-muted-foreground">(does not block saving)</span>
              </div>
              <ul className="list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
                {conditionWarnings.map((m, i) => (
                  <li key={`c${i}`}>{m}</li>
                ))}
              </ul>
              {serverWarnings.length > 0 && (
                <>
                  <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    From the last save
                  </div>
                  <ul className="list-inside list-disc space-y-0.5 text-sm text-muted-foreground">
                    {serverWarnings.map((m, i) => (
                      <li key={`s${i}`}>{m}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
          {draft.childSteps.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border py-8 text-center">
              <p className="text-sm font-medium text-foreground">No steps yet</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                This request type will create a single ticket with no child tickets.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {draft.childSteps.map((s, i) => (
                <FlowStepRow
                  key={s._id}
                  step={s}
                  index={i}
                  total={draft.childSteps.length}
                  earlierSteps={draft.childSteps.slice(0, i).map((es, ei) => ({ ...es, index: ei }))}
                  intakeFields={draft.intakeFields}
                  functions={functions}
                  keyError={stepKeyErrors[s._id]}
                  onChange={(patch) => patchStep(s._id, patch)}
                  onMove={(dir) => moveStep(i, dir)}
                  onDelete={() => deleteStep(i)}
                />
              ))}
            </div>
          )}
        </section>

        {/* (d) Preview */}
        <section className="surface rounded-xl ring-1 ring-border p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <Eye className="h-5 w-5 text-brand" />
            What this generates
          </h2>
          <FlowPreview
            requestTypeLabel={requestTypeLabel}
            intakeFields={draft.intakeFields}
            steps={draft.childSteps}
            functions={functions}
            conditionTextById={conditionTextById}
          />
        </section>
      </div>

      <ConfirmDialog
        open={activateConfirm}
        title="Activate this template?"
        description={`New ${requestTypeLabel} tickets${
          variantName && variantName !== requestTypeLabel ? ` on the “${variantName}” variant` : ""
        } will start generating ${draft.childSteps.length} ${
          draft.childSteps.length === 1 ? "step" : "steps"
        }. Any previously live version of this variant is replaced; other variants stay live.${
          typeVariants.some((v) => v.isDefault) ? "" : " It becomes the request type's default."
        }`}
        confirmLabel="Save & activate"
        onCancel={() => setActivateConfirm(false)}
        onConfirm={() => {
          setActivateConfirm(false);
          save({ activate: true });
        }}
      />

      <ConfirmDialog
        open={!!discardConfirm}
        title="Discard unsaved changes?"
        description="Your unsaved edits to this template will be lost."
        confirmLabel="Discard"
        variant="destructive"
        onCancel={() => setDiscardConfirm(false)}
        onConfirm={() => {
          const target = discardConfirm;
          setDiscardConfirm(false);
          // `true` = leave to the list; a string = the URL that asked (New
          // version / Add variant).
          if (typeof target === "string") navigate(target);
          else leave();
        }}
      />
    </div>
  );
}

export default requireAuth(WorkflowEditorRoute);
